import { useEffect, useState } from "react";
import axios from "axios";

export const describeError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

// Loading, editing and saving for an admin-editable page (see server/src/sitePages/sitePages.js).
// Until the page has been saved once, `content` is the starter content passed in as `defaults`.
// `prepareDraft(content, { isStarter })` can adjust the content when editing starts (e.g. give
// list rows keys).
// `scrollToTopOnSave` is off for sections further down a page (the home page's).
export default function useEditablePage(
  slug,
  defaults,
  { prepareDraft = (content) => content, scrollToTopOnSave = true } = {},
) {
  const [content, setContent] = useState(null); // null while loading
  const [isStarter, setIsStarter] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null); // when it was last saved (ISO string)
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(null); // non-null while editing
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`/api/pages/${slug}`)
      .then((response) => {
        if (cancelled) return;
        setContent(response.data.content ?? defaults);
        setIsStarter(!response.data.content);
        setUpdatedAt(response.data.updatedAt ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Still show the page; only saving depends on the server.
        setContent(defaults);
        setIsStarter(true);
        setLoadError(describeError(err, "The latest version of this page could not be loaded."));
      });
    return () => {
      cancelled = true;
    };
    // defaults is a module-level constant in each page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const startEditing = () => {
    setSaveError(null);
    setDraft(prepareDraft(content, { isStarter }));
  };

  const cancelEditing = () => {
    if (!window.confirm("Discard your changes to this page?")) return;
    setDraft(null);
  };

  // Resolves to true once saved, false if saving failed (the error is in saveError).
  const save = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await axios.put(`/api/pages/${slug}`, { content: draft });
      setContent(response.data.content);
      setIsStarter(false);
      setUpdatedAt(response.data.updatedAt ?? null);
      setDraft(null);
      if (scrollToTopOnSave) window.scrollTo({ top: 0, behavior: "smooth" });
      return true;
    } catch (err) {
      setSaveError(describeError(err, "The page could not be saved."));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    content,
    isStarter,
    updatedAt,
    loadError,
    draft,
    setDraft,
    isSaving,
    saveError,
    startEditing,
    cancelEditing,
    save,
  };
}
